import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/EquipmentAssign.css';
import API_BASE from '../config';

function EquipmentAssignment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [adminSignModal, setAdminSignModal] = useState(null); // assignmentId pending admin sign
  const [adminTypedName, setAdminTypedName] = useState('');
  const [adminSignLoading, setAdminSignLoading] = useState(false);
  const [adminSignError, setAdminSignError] = useState('');
  const [serverStatus, setServerStatus] = useState('checking');
  const [clients, setClients] = useState([]);
  const [awaitingCandidates, setAwaitingCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [currentEquipment, setCurrentEquipment] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [replacingItems, setReplacingItems] = useState({});
  const [lookupResult, setLookupResult] = useState(null); // { found, employeeName, employeeEmail, department, activeAssignments }
  const [existingEquipWarning, setExistingEquipWarning] = useState(false); // shown when type=new & employee already has active kit
  const [assetSuggestions, setAssetSuggestions] = useState({});  // key → asset[]  from Asset Management search
  const [suggestionsOpen, setSuggestionsOpen] = useState({});    // key → bool

  // Ping health endpoint on mount so Render wakes up before the user submits
  useEffect(() => {
    const wakeServer = async () => {
      try {
        const res = await fetch(`${API_BASE}/health`, { method: 'GET' });
        if (res.ok) setServerStatus('online');
        else setServerStatus('offline');
      } catch {
        setServerStatus('offline');
      }
    };
    wakeServer();

    // Load clients for the department dropdown
    const loadClients = async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/clients`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (res.ok) setClients(data.clients || []);
      } catch { /* non-fatal */ }
    };
    loadClients();

    // Load hired candidates awaiting equipment
    const loadAwaiting = async () => {
      try {
        const res = await fetch(`${API_BASE}/recruitment/hired-awaiting`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (res.ok) setAwaitingCandidates(data.candidates || []);
      } catch { /* non-fatal */ }
    };
    loadAwaiting();
  }, []);

  const [formData, setFormData] = useState({
    employeeId: '',
    employeeName: '',
    employeeEmail: '',
    department: '',
    receiveMethod: 'delivery',
    address: '',
    telephone: '',

    equipmentType: 'new',
    computer: '',
    monitor1: '',
    monitor2: '',
    token: '',
    other: ''
  });

  const [enabled, setEnabled] = useState({
    computer: false, monitor1: false, monitor2: false, token: false, other: false
  });

  // Refs for scanner Enter-to-advance
  const employeeIdRef = useRef(null);
  const employeeNameRef = useRef(null);
  const serialRefs = {
    computer: useRef(null),
    monitor1: useRef(null),
    monitor2: useRef(null),
    token:    useRef(null),
    other:    useRef(null),
  };
  const SERIAL_KEYS = ['computer', 'monitor1', 'monitor2', 'token', 'other'];

  const focusNextSerial = (currentKey) => {
    const startIdx = SERIAL_KEYS.indexOf(currentKey) + 1;
    for (let i = startIdx; i < SERIAL_KEYS.length; i++) {
      if (enabled[SERIAL_KEYS[i]] && serialRefs[SERIAL_KEYS[i]].current) {
        serialRefs[SERIAL_KEYS[i]].current.focus();
        return true;
      }
    }
    return false;
  };

  const handleSerialKeyDown = (e, key) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      focusNextSerial(key);
    }
  };

  const handleEmployeeIdKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      employeeNameRef.current && employeeNameRef.current.focus();
    }
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // Asset Management autocomplete — search by serial/name when typing in equipment fields
  const handleSerialChange = async (key, value) => {
    set(key, value);
    if (value.length < 2) {
      setSuggestionsOpen(prev => ({ ...prev, [key]: false }));
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/admin/assets?search=${encodeURIComponent(value)}&limit=6`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const d = await res.json();
        const hits = (d.assets || []).filter(a => !a.deletedAt);
        setAssetSuggestions(prev => ({ ...prev, [key]: hits }));
        setSuggestionsOpen(prev => ({ ...prev, [key]: hits.length > 0 }));
      }
    } catch { /* non-fatal */ }
  };

  // Debounced employee ID lookup — runs for all equipment types
  useEffect(() => {
    setLookupResult(null);
    setExistingEquipWarning(false);
    if (formData.equipmentType !== 'change') {
      setCurrentEquipment([]);
      setReplacingItems({});
    }
    if (!formData.employeeId || formData.employeeId.length < 3) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingEquipment(true);
      try {
        const res = await fetch(`${API_BASE}/admin/users/lookup/${formData.employeeId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.found) {
          setLookupResult(data);
          // Auto-populate empty fields from the lookup
          setFormData(prev => ({
            ...prev,
            employeeName:  prev.employeeName  || data.employeeName  || prev.employeeName,
            employeeEmail: prev.employeeEmail || data.employeeEmail || prev.employeeEmail,
            department:    prev.department    || data.department    || prev.department,
          }));
          // For change type, populate replacement picker
          if (formData.equipmentType === 'change') {
            setCurrentEquipment(data.activeAssignments || []);
          }
          // For new type, warn if employee already has active equipment
          if (formData.equipmentType === 'new' && data.activeAssignments?.length > 0) {
            setExistingEquipWarning(true);
          }
        } else {
          // No user found — if change type, fall back to assignments endpoint
          if (formData.equipmentType === 'change') {
            const r2 = await fetch(`${API_BASE}/admin/equipment-assignments/employee/${formData.employeeId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const d2 = await r2.json();
            if (!cancelled) setCurrentEquipment(r2.ok && d2.assignments?.length > 0 ? d2.assignments : []);
          }
        }
      } catch { /* non-fatal */ }
      finally { if (!cancelled) setLoadingEquipment(false); }
    }, 600); // 600ms debounce
    return () => { cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.employeeId, formData.equipmentType]);

  // Pre-fill form from a hired candidate tile
  const prefillFromCandidate = (c) => {
    setSelectedCandidate(c);
    setFormData(prev => ({
      ...prev,
      employeeId:    c.assignedEmployeeId || '',
      employeeName:  c.fullName || '',
      employeeEmail: c.email || '',
      address:       c.address || '',
      telephone:     c.telephone || '',
      department:    c.campaign || '',
      receiveMethod: c.address ? 'delivery' : 'self_pickup',
    }));
    // Scroll to form
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const toggleItem = (key) => {
    setEnabled(prev => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next[key]) setFormData(p => ({ ...p, [key]: '' }));
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.employeeId) { setError('Please enter an Employee ID'); setLoading(false); return; }

    const payload = {
      ...formData,
      telephone: formData.receiveMethod === 'delivery' ? formData.telephone : '',
      computer:  enabled.computer  ? formData.computer  : '',
      monitor1:  enabled.monitor1  ? formData.monitor1  : '',
      monitor2:  enabled.monitor2  ? formData.monitor2  : '',
      token:     enabled.token     ? formData.token     : '',
      other:     enabled.other     ? formData.other     : '',
      replacingItems: Object.values(replacingItems).filter(Boolean),
    };

    const attemptSubmit = async (attemptsLeft) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout
        const res = await fetch(`${API_BASE}/admin/equipment-assignment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify(payload),
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        const data = await res.json();
        if (res.ok) {
          // If this was pre-filled from a hired candidate, mark them as equipment assigned
          if (selectedCandidate) {
            fetch(`${API_BASE}/recruitment/candidates/${selectedCandidate.id}/mark-equipment-assigned`, {
              method: 'PATCH', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            }).catch(() => {});
            setSelectedCandidate(null);
          }
          setFormData({ employeeId:'', employeeName:'', employeeEmail:'', department:'', receiveMethod:'delivery', address:'', equipmentType:'new', computer:'', monitor1:'', monitor2:'', token:'', other:'' });
          setEnabled({ computer:false, monitor1:false, monitor2:false, token:false, other:false });
          setReplacingItems({});
          setCurrentEquipment([]);
          setLookupResult(null);
          setExistingEquipWarning(false);
          // Open admin signing modal — admin must sign before email goes to employee
          setAdminTypedName('');
          setAdminSignError('');
          setAdminSignModal(data.assignmentId);
        } else if (res.status === 401 || res.status === 403) {
          setError(data.message || 'Access denied. Please log in again.');
        } else {
          setError(data.message || 'Failed to create assignment');
        }
      } catch (err) {
        if (attemptsLeft > 0) {
          // Server may be waking up (Render cold start) – retry after 5 seconds
          setError(`Server is starting up, retrying... (${attemptsLeft} attempt${attemptsLeft > 1 ? 's' : ''} left)`);
          await new Promise(r => setTimeout(r, 5000));
          return attemptSubmit(attemptsLeft - 1);
        }
        if (err.name === 'AbortError') {
          setError('Request timed out (60s). The server is taking too long — please try again.');
        } else if (err.message === 'Failed to fetch') {
          setError(`Cannot reach the server at ${API_BASE}. The server may be starting up – please wait 30 seconds and try again.`);
        } else {
          setError(err.message || 'An error occurred');
        }
      }
    };

    try {
      await attemptSubmit(2); // try up to 3 times total
    } finally {
      setLoading(false);
    }
  };

  const ITEMS = [
    { key: 'computer', label: 'PC / Computer',         ph: 'Scan or type serial / model...' },
    { key: 'monitor1', label: 'Monitor 1',              ph: 'Scan or type serial / model...' },
    { key: 'monitor2', label: 'Monitor 2',              ph: 'Scan or type serial / model...' },
    { key: 'token',    label: 'MFA Token / OneSpan',    ph: 'Scan or type token serial...' },
    { key: 'other',    label: 'Other',                  ph: 'Scan or type description...' },
  ];

  return (
    <div className="eq-assign-page">
      <div className="eq-assign-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>&larr; Back</button>
        <div>
          <h1>Assign Equipment</h1>
          <p className="subtitle">Issue company hardware to an employee and generate a handover protocol</p>
        </div>
      </div>

      {serverStatus === 'checking' && <div className="eq-assign-alert" style={{background:'#fff7e0',color:'#856404',border:'1px solid #ffc107'}}>⏳ Connecting to server...</div>}
      {serverStatus === 'offline'  && <div className="eq-assign-alert" style={{background:'#fff3cd',color:'#856404',border:'1px solid #ffc107'}}>⚠️ Server unreachable at <b>{API_BASE}</b>. It may be starting up — wait 30 seconds and try submitting.</div>}
      {error   && <div className="eq-assign-alert err">{error}</div>}
      {success && <div className="eq-assign-alert ok">&#10004; {success}</div>}

      {/* ── AWAITING EQUIPMENT PANEL ──────────────────────────── */}
      {awaitingCandidates.length > 0 && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '16px 20px', marginBottom: 22 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1e40af', marginBottom: 12 }}>
            🎯 Awaiting Equipment Assignment ({awaitingCandidates.length})
          </div>
          <p style={{ fontSize: 13, color: '#3b5bdb', margin: '0 0 12px 0' }}>
            These candidates have signed their onboarding documents. Click a tile to pre-fill the form.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {awaitingCandidates.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => prefillFromCandidate(c)}
                style={{
                  background: selectedCandidate?.id === c.id ? '#1e40af' : '#fff',
                  color:      selectedCandidate?.id === c.id ? '#fff' : '#1e3a8a',
                  border:     `2px solid ${selectedCandidate?.id === c.id ? '#1e40af' : '#93c5fd'}`,
                  borderRadius: 8, padding: '12px 14px', cursor: 'pointer', textAlign: 'left',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>{c.fullName}</div>
                {c.campaign  && <div style={{ fontSize: 12, opacity: 0.8 }}>📋 {c.campaign}</div>}
                {c.telephone && <div style={{ fontSize: 12, opacity: 0.8 }}>📞 {c.telephone}</div>}
                {c.country   && <div style={{ fontSize: 12, opacity: 0.8 }}>🌍 {c.country.name}</div>}
              </button>
            ))}
          </div>
          {selectedCandidate && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#166534', fontWeight: 600 }}>
              ✔ Pre-filled from <strong>{selectedCandidate.fullName}</strong>. Complete the serial numbers and submit.
              <button
                type="button"
                onClick={() => { setSelectedCandidate(null); setFormData(p => ({ ...p, employeeId:'', employeeName:'', employeeEmail:'', address:'', telephone:'' })); }}
                style={{ marginLeft: 12, fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear pre-fill
              </button>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Card 1 - Employee */}
        <div className="eq-assign-card">
          <h2 className="card-title">Step 1 &mdash; Employee Information</h2>

          <div className="field-row two-col">
            <div className="field-group">
              <label>Employee ID (Success Factor) *</label>
              <input
                ref={employeeIdRef}
                className="eq-input"
                type="text"
                inputMode="text"
                placeholder="Scan or type ID..."
                value={formData.employeeId}
                onChange={e => set('employeeId', e.target.value)}
                onKeyDown={handleEmployeeIdKeyDown}
                required
              />
            </div>
            <div className="field-group">
              <label>Full Name *</label>
              <input
                ref={employeeNameRef}
                className="eq-input"
                type="text"
                placeholder="e.g. John Smith"
                value={formData.employeeName}
                onChange={e => set('employeeName', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Lookup feedback */}
          {loadingEquipment && formData.employeeId.length >= 3 && (
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, marginTop: -4 }}>&#128269; Looking up employee…</div>
          )}
          {!loadingEquipment && lookupResult?.found && (
            <div style={{ fontSize: 12, color: '#16a34a', marginBottom: 6, marginTop: -4 }}>&#10003; Employee found — fields auto-populated.</div>
          )}

          {/* Warning: this employee already has active equipment and type = new */}
          {existingEquipWarning && formData.equipmentType === 'new' && (
            <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
              <strong style={{ color: '#c2410c', fontSize: 13 }}>&#9888; This employee already has {lookupResult.activeAssignments.length} active equipment assignment(s).</strong>
              <p style={{ margin: '4px 0 6px', fontSize: 12, color: '#9a3412' }}>
                Are you sure you want to create a <em>new</em> assignment? Consider using <strong>Change / Replacement</strong> instead.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => set('equipmentType', 'change')}
                  style={{ fontSize: 12, padding: '4px 10px', background: '#ea580c', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                  Switch to Change / Replacement
                </button>
                <button type="button" onClick={() => setExistingEquipWarning(false)}
                  style={{ fontSize: 12, padding: '4px 10px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>
                  Continue as New anyway
                </button>
              </div>
            </div>
          )}
          <div className="field-group">
            <label>Employee Email <span className="label-optional">(optional — protocol will be sent automatically)</span></label>
            <input
              className="eq-input"
              type="email"
              placeholder="employee@company.com"
              value={formData.employeeEmail}
              onChange={e => set('employeeEmail', e.target.value)}
            />
          </div>

          <div className="field-group">
            <label>Department *</label>
            <select className="eq-input" value={formData.department} onChange={e => set('department', e.target.value)} required>
              <option value="">Select department...</option>
              <optgroup label="Clients">
                {clients.length === 0 && <option disabled>No clients — add them in Client Management</option>}
                {clients.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </optgroup>
              <optgroup label="Internal">
                <option value="IT">IT</option>
                <option value="HR">HR</option>
              </optgroup>
            </select>
          </div>

          <div className="field-group">
            <label>Receive Method *</label>
            <div className="pill-group">
              {[['delivery', 'Delivery'], ['self_pickup', 'Self Pickup from Office']].map(([v, lbl]) => (
                <button key={v} type="button"
                  className={`pill-btn ${formData.receiveMethod === v ? 'active' : ''}`}
                  onClick={() => set('receiveMethod', v)}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {formData.receiveMethod === 'delivery' && (
            <>
              <div className="field-group">
                <label>Delivery Address *</label>
                <textarea
                  className="eq-input"
                  rows={3}
                  placeholder="Street, City, Postcode..."
                  value={formData.address}
                  onChange={e => set('address', e.target.value)}
                  required
                />
              </div>
              <div className="field-group">
                <label>Telephone <span className="label-optional">(optional — for delivery coordination)</span></label>
                <input
                  className="eq-input"
                  type="tel"
                  placeholder="+49 170 1234567"
                  value={formData.telephone}
                  onChange={e => set('telephone', e.target.value)}
                />
              </div>
            </>
          )}

        </div>

        {/* Card 2 - Equipment */}
        <div className="eq-assign-card">
          <h2 className="card-title">Step 2 &mdash; Equipment Details</h2>

          <div className="field-group">
            <label>Equipment Type *</label>
            <div className="pill-group">
              {[['new', 'New Equipment'], ['change', 'Change / Replacement']].map(([v, lbl]) => (
                <button key={v} type="button"
                  className={`pill-btn ${formData.equipmentType === v ? 'active' : ''}`}
                  onClick={() => set('equipmentType', v)}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {formData.equipmentType === 'change' && (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: 16, marginTop: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 8 }}>
                🔄 Select Items to Replace
              </div>
              {!formData.employeeId && (
                <div style={{ fontSize: 13, color: '#6b7280' }}>Enter an Employee ID above to see their current equipment.</div>
              )}
              {formData.employeeId && loadingEquipment && (
                <div style={{ fontSize: 13, color: '#6b7280' }}>Looking up current equipment…</div>
              )}
              {formData.employeeId && !loadingEquipment && currentEquipment.length === 0 && (
                <div style={{ fontSize: 13, color: '#6b7280' }}>No active equipment found for this employee. Toggle items manually below.</div>
              )}
              {currentEquipment.map(assignment => {
                const SLOT_LABELS = { computer: 'PC / Computer', monitor1: 'Monitor 1', monitor2: 'Monitor 2', token: 'MFA Token', other: 'Other' };
                const slots = ['computer', 'monitor1', 'monitor2', 'token', 'other'].filter(k => assignment[k] && assignment[k] !== 'NA');
                if (slots.length === 0) return null;
                return (
                  <div key={assignment.id} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: '#92400e', marginBottom: 6, fontWeight: 600 }}>
                      Assignment #{assignment.id} — {new Date(assignment.createdAt).toLocaleDateString('en-GB')}
                    </div>
                    {slots.map(key => {
                      const itemKey = `${assignment.id}_${key}`;
                      const isChecked = !!replacingItems[itemKey];
                      return (
                        <label key={key} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px',
                          marginBottom: 5, borderRadius: 6, cursor: 'pointer',
                          background: isChecked ? '#fee2e2' : '#fefce8',
                          border: `1px solid ${isChecked ? '#fca5a5' : '#fde68a'}`
                        }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={e => {
                              if (e.target.checked) {
                                setReplacingItems(prev => ({ ...prev, [itemKey]: { assignmentId: assignment.id, slot: key, prevValue: assignment[key] } }));
                                setEnabled(prev => ({ ...prev, [key]: true }));
                                set(key, '');
                              } else {
                                setReplacingItems(prev => { const n = { ...prev }; delete n[itemKey]; return n; });
                              }
                            }}
                          />
                          <span style={{ fontSize: 13, fontWeight: 600, minWidth: 120, color: '#374151' }}>
                            {SLOT_LABELS[key]}
                          </span>
                          <span style={{ fontSize: 13, color: '#6b7280' }}>
                            Current: <strong style={{ color: '#dc2626', textDecoration: isChecked ? 'line-through' : 'none' }}>{assignment[key]}</strong>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          <p className="field-hint">Toggle each item, then scan or type its serial. <span className="scan-hint">Scanner: Enter key auto-advances to the next field.</span></p>

          <div className="items-list">
            {ITEMS.map(({ key, label, ph }) => (
              <div key={key} className={`item-row ${enabled[key] ? 'item-row-on' : ''}`}>
                <button type="button" className={`item-toggle ${enabled[key] ? 'toggle-on' : 'toggle-off'}`}
                  onClick={() => toggleItem(key)}>
                  <span className="toggle-icon">{enabled[key] ? '\u2714' : '+'}</span>
                  <span className="item-label">{label}</span>
                </button>
                {enabled[key] && (
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      ref={serialRefs[key]}
                      className="eq-input item-serial-input"
                      type="text"
                      placeholder={ph}
                      value={formData[key]}
                      onChange={e => handleSerialChange(key, e.target.value)}
                      onKeyDown={e => handleSerialKeyDown(e, key)}
                      onBlur={() => setTimeout(() => setSuggestionsOpen(prev => ({ ...prev, [key]: false })), 180)}
                      autoFocus
                    />
                    {suggestionsOpen[key] && assetSuggestions[key]?.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.13)', maxHeight: 230, overflowY: 'auto' }}>
                        {assetSuggestions[key].map(asset => (
                          <div
                            key={asset.id}
                            onMouseDown={() => {
                              set(key, asset.serialNumber || '');
                              setSuggestionsOpen(prev => ({ ...prev, [key]: false }));
                            }}
                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                          >
                            <div style={{ fontWeight: 700, color: '#1d4ed8', fontSize: 13 }}>{asset.serialNumber || '(no serial)'}</div>
                            <div style={{ color: '#6b7280', fontSize: 11 }}>{asset.name}{asset.model ? ` · ${asset.model}` : ''} &nbsp;·&nbsp; <span style={{ color: asset.status === 'IN_STORAGE' ? '#15803d' : '#64748b' }}>{asset.status}</span></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="submit-row">
          <button type="button" className="btn-cancel" onClick={() => navigate('/admin')} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? 'Creating...' : 'Create Assignment & Generate Protocol'}
          </button>
        </div>
      </form>

      {/* ── ADMIN SIGNING MODAL ──────────────────────────────────────── */}
      {adminSignModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 36, maxWidth: 460, width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 26 }}>✍️</span>
              <h2 style={{ margin: 0, color: '#1a237e', fontSize: '1.25rem' }}>Sign the Protocol</h2>
            </div>
            <p style={{ color: '#555', fontSize: 14, marginBottom: 18, lineHeight: 1.6 }}>
              Assignment <strong>#{adminSignModal}</strong> has been created. As the IT administrator, you must
              digitally sign the protocol before it is sent to the employee for their signature.
            </p>
            <label style={{ fontWeight: 600, fontSize: 13, color: '#374151', display: 'block', marginBottom: 6 }}>
              Type your full name to sign *
            </label>
            <input
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                borderRadius: 8, fontSize: 15, boxSizing: 'border-box',
                outline: 'none', marginBottom: 14
              }}
              placeholder="Your full name..."
              value={adminTypedName}
              onChange={e => setAdminTypedName(e.target.value)}
              autoFocus
            />
            {adminSignError && (
              <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{adminSignError}</p>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{
                  flex: 1, padding: '11px 0', background: '#1a237e', color: '#fff',
                  border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer'
                }}
                disabled={adminSignLoading}
                onClick={async () => {
                  if (!adminTypedName.trim()) { setAdminSignError('Please type your full name.'); return; }
                  setAdminSignLoading(true);
                  setAdminSignError('');
                  try {
                    const res = await fetch(`${API_BASE}/admin/equipment-assignments/${adminSignModal}/admin-sign`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
                      body: JSON.stringify({ typedName: adminTypedName.trim() })
                    });
                    const data = await res.json();
                    if (res.ok) {
                      if (data.emailResult?.sent) {
                        setSuccess('Protocol signed. Email with sign link sent to employee.');
                      } else {
                        const reason = data.emailResult?.reason || 'check employee email field and Resend API key';
                        setAdminSignError(`Protocol signed, but email was NOT sent (${reason}). Navigate to Equipment Log to resend manually.`);
                      }
                      setAdminSignModal(null);
                      setTimeout(() => navigate('/admin'), data.emailResult?.sent ? 2200 : 6000);
                    } else {
                      setAdminSignError(data.message || 'Failed to sign protocol.');
                    }
                  } catch {
                    setAdminSignError('Network error. Please try again.');
                  } finally {
                    setAdminSignLoading(false);
                  }
                }}
              >
                {adminSignLoading ? 'Signing...' : '✓ Sign & Send to Employee'}
              </button>
              <button
                style={{
                  padding: '11px 20px', background: '#f3f4f6', color: '#374151',
                  border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer'
                }}
                disabled={adminSignLoading}
                onClick={() => { setAdminSignModal(null); setSuccess('Assignment created. Sign the protocol later from Equipment Log.'); setTimeout(() => navigate('/admin'), 2200); }}
              >
                Later
              </button>
            </div>
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 12, textAlign: 'center' }}>
              Your name, timestamp, and IP address will be recorded on the protocol PDF.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default EquipmentAssignment;
